import {
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { DatePicker } from "@mui/x-date-pickers";
import usePricingManagamentAnalyzerStore, { PricingManagementFilterInput } from "../../../stores/pricing-management-analyzer.store.ts";
import { NumericFormat } from "react-number-format";
import {
  RatingAssumption,
  TaxDesignation,
} from "../../../deal-builder/services/utils/primary-constants.ts";
import FilterAltIcon from '@mui/icons-material/FilterAlt';

export function FiltersButton() {
  const [openDialog, setOpenDialog] = useState<boolean>(false);

  const filters = usePricingManagamentAnalyzerStore();

  function onUpdate(newFilters: PricingManagementFilterInput) {
    filters.updateAll(newFilters);
    setOpenDialog(false)
  }

  return (
    <React.Fragment>
      <Button
        variant="outlined"
        color="primary"
        onClick={() => setOpenDialog(true)}
        startIcon={<FilterAltIcon />}
      >
        Filter Deals
      </Button>
      {openDialog && (
        <FiltersPopup 
          filters={filters}
          onChange={onUpdate}
          onClose={() => setOpenDialog(false)}
        />
      )}
    </React.Fragment>
  );
}

interface FiltersPopupProps {
  filters: PricingManagementFilterInput,
  onChange: (filters: PricingManagementFilterInput) => void,
  onClose: () => void,
}

function FiltersPopup({ filters, onChange, onClose }: FiltersPopupProps) {
  const [newFilters, setNewFilters] = useState({ ...filters });

  useEffect(() => {
    setNewFilters({  ...filters })
  }, [filters])

  return (
    <Dialog
      open={true}
      onClose={onClose}
      maxWidth="md"
      fullWidth={true}
    >
      <DialogTitle>Filters</DialogTitle>

      <DialogContent>
        <Stack direction="column" gap={2} py={2}>
          <Stack direction="row" gap={2}>
            <DatePicker
              label="Min BID Date"
              format="MM/DD/YYYY"
              value={newFilters.minDate}
              slotProps={{ textField: { sx: { width: '100%' }} }}
              onChange={(val) => {
                if (!val || !val.isValid) return;
                setNewFilters({ ...newFilters, minDate: val });
              }}
            />
            <DatePicker
              label="Max BID Date"
              format="MM/DD/YYYY"
              value={newFilters.maxDate}
              slotProps={{ textField: { sx: { width: '100%' }} }}
              onChange={(val) => {
                if (!val || !val.isValid) return;
                setNewFilters({ ...newFilters, maxDate: val });
              }}
            />
          </Stack>

          <Stack direction="row" gap={2}>
            <NumericFormat
              label="Min Maturity"
              value={newFilters.minMaturity}
              fullWidth
              onValueChange={(values) => {
                setNewFilters({ ...newFilters, minMaturity: values.floatValue || 0 });
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">Y</InputAdornment>
                ),
              }}
              customInput={TextField}
              decimalScale={2}
              thousandSeparator
              allowLeadingZeros={false}
            />
            <NumericFormat
              label="Max Maturity"
              value={newFilters.maxMaturity}
              fullWidth
              onValueChange={(values) => {
                setNewFilters({ ...newFilters, maxMaturity: values.floatValue || 0 });
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">Y</InputAdornment>
                ),
              }}
              customInput={TextField}
              decimalScale={2}
              thousandSeparator
              allowLeadingZeros={false}
            />
          </Stack>

          <Stack direction="row" gap={2}>
            <FormControl fullWidth>
              <InputLabel>Min Rating</InputLabel>
              <Select
                value={newFilters.minRating}
                label="Min Rating"
                onChange={(e) => {
                  const rating = e.target.value as RatingAssumption;
                  setNewFilters({ ...newFilters, minRating: rating });
                }}
              >
                {Object.values(RatingAssumption).map((val) => (
                  <MenuItem key={val} value={val}>
                    {val}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Max Rating</InputLabel>
              <Select
                value={newFilters.maxRating}
                label="Max Rating"
                onChange={(e) => {
                  const rating = e.target.value as RatingAssumption;
                  setNewFilters({ ...newFilters, maxRating: rating });
                }}
              >
                {Object.values(RatingAssumption).map((val) => (
                  <MenuItem key={val} value={val}>
                    {val}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>

          <NumericFormat
            label="Cover Bid Limit"
            value={newFilters.coverBidLimit * 100}
            customInput={TextField}
            thousandSeparator=","
            suffix="%"
            decimalScale={3}
            allowNegative={false}
            onValueChange={(values) => {
              const value = values.floatValue ? values.floatValue / 100 : 0;
              setNewFilters({ ...newFilters, coverBidLimit: value });
            }}
          />
          <Autocomplete
            multiple
            value={newFilters.taxDesignations}
            options={Object.values(TaxDesignation)}
            renderInput={(params) => (
              <TextField {...params} label="Tax Designations" />
            )}
            onChange={(e, newValue) => {
              setNewFilters({ ...newFilters, taxDesignations: newValue });
            }}
          />
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button 
          onClick={onClose} 
          color="error"
          variant="outlined"
        >
          Close
        </Button>
        <Button 
          onClick={() => onChange(newFilters)} 
          color="primary"
          variant="contained"
          sx={{ color: "white" }}
        >
          Update
        </Button>
      </DialogActions>
    </Dialog>
  )
}